import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { 
  FileText, Loader2, AlertTriangle, CheckCircle2, 
  RefreshCw, Lock, Unlock, FileCheck, AlertCircle,
  ChevronDown, ChevronUp, Download, FileDown, Share2, Copy, Link, X
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReportPanel Component
 * 
 * Operator tool for report generation and issuance (M6).
 * Also handles PDF generation (derived artifact).
 * 
 * Props:
 * - serId: Service Execution Record ID
 * - onReportUpdate: Callback when report is generated/issued
 * - m4Complete: Whether M4 milestone is complete
 * - m5Complete: Whether M5 milestone is complete
 * - lifecycleState: Current SER lifecycle state
 */
const ReportPanel = ({ serId, onReportUpdate, m4Complete = false, m5Complete = false, lifecycleState = "" }) => {
  const { token } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState(false);
  
  // PDF states
  const [pdfStatus, setPdfStatus] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Share states
  const [shareLinks, setShareLinks] = useState([]);
  const [creatingShare, setCreatingShare] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({
    expires_in_hours: 72,
    max_views: null,
    label: ''
  });
  const [newShareUrl, setNewShareUrl] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const fetchPreview = async () => {
    if (!serId || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to fetch report preview');
      }
      
      const data = await response.json();
      setPreview(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load report preview:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfStatus = async () => {
    if (!serId || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/pdf/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPdfStatus(data);
      }
    } catch (err) {
      console.error("Failed to load PDF status:", err);
    }
  };

  useEffect(() => {
    fetchPreview();
    fetchPdfStatus();
    fetchShareLinks();
  }, [serId, token]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to generate report');
      }
      
      const data = await response.json();
      toast.success(`Report generated successfully! Report ID: ${data.report_id?.slice(0, 8)}...`);
      
      // Refresh preview
      await fetchPreview();
      onReportUpdate?.();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/pdf/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to generate PDF');
      }
      
      const data = await response.json();
      
      if (data.already_exists) {
        toast.info("PDF already exists. Ready for download.");
      } else {
        toast.success(`PDF generated! ${data.page_count} pages, ${Math.round(data.file_size_bytes / 1024)}KB`);
      }
      
      await fetchPdfStatus();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/pdf/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      // Get filename from header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'AIMB_Report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  // Share link functions
  const fetchShareLinks = async () => {
    if (!serId || !token) return;
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/share/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setShareLinks(data.share_links || []);
      }
    } catch (err) {
      console.error("Failed to fetch share links:", err);
    }
  };

  const handleCreateShareLink = async () => {
    setCreatingShare(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/share/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          expires_in_hours: shareForm.expires_in_hours,
          max_views: shareForm.max_views || null,
          label: shareForm.label || null
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to create share link');
      }
      
      const data = await response.json();
      setNewShareUrl(data.share_url);
      toast.success('Share link created!');
      await fetchShareLinks();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingShare(false);
    }
  };

  const handleRevokeShareLink = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this share link?')) return;
    
    setRevokingId(tokenId);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/share/${tokenId}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to revoke share link');
      }
      
      toast.success('Share link revoked');
      await fetchShareLinks();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleIssueReport = async () => {
    if (!confirmIssue) {
      setConfirmIssue(true);
      return;
    }

    setIssuing(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/report/issue?delivery_confirmed=true`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to issue report');
      }
      
      const data = await response.json();
      
      if (data.milestone_completed) {
        toast.success("Report issued and locked! M6 milestone completed. Evidence is now immutable.");
      } else {
        toast.success("Report issued and locked! Evidence is now immutable.");
      }
      
      setConfirmIssue(false);
      
      // Refresh preview
      await fetchPreview();
      onReportUpdate?.();
      
    } catch (err) {
      toast.error(err.message);
      setConfirmIssue(false);
    } finally {
      setIssuing(false);
    }
  };

  // Check prerequisites
  const prerequisitesMet = m4Complete && m5Complete;

  if (loading) {
    return (
      <div className="glass p-6 rounded-xl" data-testid="report-panel">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-slate-400">Loading report status...</span>
        </div>
      </div>
    );
  }

  // Show prerequisites message if M4/M5 not complete
  if (!prerequisitesMet) {
    return (
      <div className="glass p-6 rounded-xl border border-slate-700" data-testid="report-panel">
        <div className="flex items-center gap-3 mb-4">
          <FileText className="w-5 h-5 text-slate-500" />
          <div>
            <h3 className="font-semibold text-slate-400">Report Generation</h3>
            <p className="text-sm text-slate-500">
              Prerequisites not met
            </p>
          </div>
        </div>
        <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-300">
              <p className="font-medium mb-1">Complete these milestones first:</p>
              <ul className="list-disc list-inside text-amber-300/80 space-y-1">
                {!m4Complete && <li>M4: Signal Extraction</li>}
                {!m5Complete && <li>M5: Assumptions Declaration</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const reportExists = preview?.report_exists;
  const reportLocked = preview?.report?.locked;
  const m6Complete = preview?.m6_complete;

  return (
    <div className="glass p-6 rounded-xl" data-testid="report-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className={`w-5 h-5 ${m6Complete ? 'text-green-400' : 'text-blue-400'}`} />
          <div>
            <h3 className="font-semibold text-white">Report Generation</h3>
            <p className="text-sm text-slate-400">
              {m6Complete 
                ? "Report issued and locked" 
                : reportExists 
                  ? "Report ready for issuance" 
                  : "Generate final engagement report"
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {m6Complete && (
            <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
              <Lock className="w-3 h-3" />
              M6 Complete
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchPreview}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {/* Report Status Card */}
      <div className={`rounded-lg p-4 border mb-4 ${
        m6Complete 
          ? 'bg-green-950/30 border-green-500/30' 
          : reportExists 
            ? 'bg-blue-950/30 border-blue-500/30' 
            : 'bg-slate-800/50 border-slate-700'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {m6Complete ? (
              <Lock className="w-4 h-4 text-green-400" />
            ) : reportExists ? (
              <FileCheck className="w-4 h-4 text-blue-400" />
            ) : (
              <Unlock className="w-4 h-4 text-slate-400" />
            )}
            <span className="text-sm font-medium text-white">
              {m6Complete 
                ? "Report Issued & Locked" 
                : reportExists 
                  ? "Draft Report Generated" 
                  : "No Report Yet"
              }
            </span>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            m6Complete 
              ? 'bg-green-500/20 text-green-400' 
              : reportExists 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'bg-slate-700 text-slate-400'
          }`}>
            {preview?.status || 'NOT_GENERATED'}
          </span>
        </div>
        
        {reportExists && preview?.report && (
          <>
            <div className="text-xs text-slate-400 mb-2">
              Business: {preview.report.business_name || 'N/A'}
            </div>
            <div className="flex gap-4 text-xs">
              <span className="text-slate-400">
                <span className="text-white">{preview.report.signal_count || 0}</span> signals
              </span>
              <span className="text-slate-400">
                <span className="text-white">{preview.report.assumption_count || 0}</span> assumptions
              </span>
              <span className="text-slate-400">
                <span className="text-white">{preview.report.sections?.length || 0}</span> sections
              </span>
            </div>

            {/* Expandable Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-xs text-primary mt-2 hover:text-primary/80"
            >
              {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showDetails ? "Hide details" : "Show details"}
            </button>
            
            {showDetails && preview.report.sections && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <p className="text-xs text-slate-400 mb-2">Report Sections:</p>
                <ul className="space-y-1">
                  {preview.report.sections.map((section, idx) => (
                    <li key={idx} className="text-xs text-slate-300 flex items-center gap-2">
                      <span className="w-4 h-4 rounded-full bg-slate-700 flex items-center justify-center text-[10px]">
                        {idx + 1}
                      </span>
                      {section}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {preview?.generated_at && (
          <div className="text-xs text-slate-500 mt-2">
            Generated: {new Date(preview.generated_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!m6Complete && (
        <div className="flex gap-3">
          {/* Generate Button */}
          <Button
            onClick={handleGenerateReport}
            disabled={generating || reportLocked}
            className={`flex-1 ${
              reportExists && !reportLocked
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-primary hover:bg-primary/90'
            }`}
            data-testid="generate-report-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : reportExists ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate Report
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Generate Report
              </>
            )}
          </Button>
          
          {/* Issue Button */}
          {reportExists && !reportLocked && (
            <Button
              onClick={handleIssueReport}
              disabled={issuing}
              className={`flex-1 ${
                confirmIssue 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
              data-testid="issue-report-btn"
            >
              {issuing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Issuing...
                </>
              ) : confirmIssue ? (
                <>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Confirm Issue & Lock
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Issue Report
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Confirmation Warning */}
      {confirmIssue && !m6Complete && (
        <div className="mt-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-300">
              <p className="font-medium mb-1">Warning: This action is irreversible</p>
              <ul className="list-disc list-inside text-red-300/80 space-y-1">
                <li>Report will be permanently locked</li>
                <li>All evidence (transcripts, signals, assumptions) will become immutable</li>
                <li>No further modifications will be allowed</li>
              </ul>
              <button
                onClick={() => setConfirmIssue(false)}
                className="text-red-400 hover:text-red-300 text-xs underline mt-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success State */}
      {m6Complete && (
        <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-300">
              <p className="font-medium">Report Successfully Issued</p>
              <p className="text-green-300/80 text-xs mt-1">
                The engagement report is now final. All evidence has been locked 
                and is immutable for audit purposes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Section - Show when report is locked (M6 complete or REPORT_ISSUED/CLOSED state) */}
      {(m6Complete || reportLocked || lifecycleState === "REPORT_ISSUED" || lifecycleState === "CLOSED") && (
        <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="pdf-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileDown className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-white">PDF Report</span>
            </div>
            {pdfStatus?.has_pdf && (
              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
                Ready
              </span>
            )}
          </div>
          
          {pdfStatus?.has_pdf ? (
            <div className="space-y-3">
              {/* PDF Info */}
              <div className="flex gap-4 text-xs text-slate-400">
                <span>{pdfStatus.pdf_metadata?.page_count || 0} pages</span>
                <span>{Math.round((pdfStatus.pdf_metadata?.file_size_bytes || 0) / 1024)} KB</span>
                <span>SHA: {pdfStatus.pdf_metadata?.pdf_sha256?.slice(0, 8)}...</span>
              </div>
              
              {/* Download Button */}
              <Button
                onClick={handleDownloadPdf}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF Report
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-400">
                Generate a PDF from the locked report for download and sharing.
              </p>
              <Button
                onClick={handleGeneratePdf}
                disabled={generatingPdf}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="generate-pdf-btn"
              >
                {generatingPdf ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4 mr-2" />
                    Generate PDF
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Share Section - Show when PDF exists */}
      {pdfStatus?.has_pdf && (lifecycleState === "REPORT_ISSUED" || lifecycleState === "CLOSED") && (
        <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="share-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Share2 className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-white">Share Report</span>
            </div>
            <span className="text-xs text-slate-400">{shareLinks.length} active link(s)</span>
          </div>

          {/* New Share URL Display */}
          {newShareUrl && (
            <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
              <p className="text-xs text-green-400 mb-2">Share link created! Copy and share:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newShareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm text-white font-mono text-xs"
                />
                <Button
                  size="sm"
                  onClick={() => copyToClipboard(newShareUrl)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <button
                onClick={() => setNewShareUrl(null)}
                className="text-xs text-slate-400 hover:text-white mt-2"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Create Share Link */}
          {!showShareModal ? (
            <Button
              onClick={() => setShowShareModal(true)}
              className="w-full bg-purple-600 hover:bg-purple-700 mb-3"
              data-testid="create-share-btn"
            >
              <Link className="w-4 h-4 mr-2" />
              Create Share Link
            </Button>
          ) : (
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white">New Share Link</span>
                <button onClick={() => setShowShareModal(false)}>
                  <X className="w-4 h-4 text-slate-400 hover:text-white" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Expires In</label>
                  <select
                    value={shareForm.expires_in_hours}
                    onChange={(e) => setShareForm(f => ({...f, expires_in_hours: parseInt(e.target.value)}))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                  >
                    <option value={24}>24 hours</option>
                    <option value={72}>3 days</option>
                    <option value={168}>7 days</option>
                    <option value={720}>30 days</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Max Views (optional)</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={shareForm.max_views || ''}
                    onChange={(e) => setShareForm(f => ({...f, max_views: e.target.value ? parseInt(e.target.value) : null}))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Label (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Sent to CFO"
                    value={shareForm.label}
                    onChange={(e) => setShareForm(f => ({...f, label: e.target.value}))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                  />
                </div>
                
                <Button
                  onClick={handleCreateShareLink}
                  disabled={creatingShare}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {creatingShare ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4 mr-2" />
                      Create Link
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Existing Share Links */}
          {shareLinks.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-slate-400 mb-2">Active Share Links:</p>
              {shareLinks.map((link) => (
                <div 
                  key={link.token_id} 
                  className={`p-2 rounded border ${
                    link.status === 'ACTIVE' 
                      ? 'bg-slate-900/50 border-slate-700' 
                      : 'bg-slate-900/30 border-slate-800 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-300">{link.token_prefix}...</span>
                      {link.label && (
                        <span className="text-xs text-slate-400">({link.label})</span>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      link.status === 'ACTIVE' ? 'bg-green-500/20 text-green-400' :
                      link.status === 'EXPIRED' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {link.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                    <span>{link.view_count} views, {link.download_count} downloads</span>
                    {link.status === 'ACTIVE' && (
                      <button
                        onClick={() => handleRevokeShareLink(link.token_id)}
                        disabled={revokingId === link.token_id}
                        className="text-red-400 hover:text-red-300"
                      >
                        {revokingId === link.token_id ? 'Revoking...' : 'Revoke'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Note */}
      {!m6Complete && (
        <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
          <p className="text-xs text-slate-400">
            <FileText className="w-3 h-3 inline mr-1" />
            Generate the report from BCO data (signals, assumptions, context). 
            Once issued, all evidence becomes immutable and the engagement transitions to REPORT_ISSUED state.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportPanel;
